import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { buildAbsoluteUrl, buildPath } from '@/lib/basePath';

interface CustomerProfile {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  partner_name?: string;
  wedding_date?: string;
  organization_id: string;
}

interface CustomerAuthContextType {
  user: User | null;
  session: Session | null;
  customer: CustomerProfile | null;
  loading: boolean;
  signInWithMagicLink: (email: string) => Promise<{ error: any }>;
  signInWithCode: (email: string, code: string) => Promise<{ error: any; session?: any }>;
  sendLoginCode: (email: string) => Promise<{ error: any; success?: boolean }>;
  refreshSession: () => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  refetchCustomer: () => Promise<void>;
}

const CustomerAuthContext = createContext<CustomerAuthContextType | undefined>(undefined);

export const useCustomerAuth = () => {
  const context = useContext(CustomerAuthContext);
  if (context === undefined) {
    throw new Error('useCustomerAuth must be used within a CustomerAuthProvider');
  }
  return context;
};

interface CustomerAuthProviderProps {
  children: ReactNode;
}

export const CustomerAuthProvider = ({ children }: CustomerAuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [customer, setCustomer] = useState<CustomerProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchCustomer = async (userEmail: string) => {
    try {
      console.log('Fetching customer for user email:', userEmail);
      
      // Find customer by email (this is the correct approach for customer portal)
      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .eq('email', userEmail)
        .maybeSingle();

      console.log('Customer lookup result:', { data, error });

      if (error) {
        console.error('Error fetching customer:', error);
        setCustomer(null);
        return;
      }

      if (!data) {
        console.warn('No customer found for email:', userEmail);
        setCustomer(null);
        return;
      }

      console.log('Customer found:', data);
      setCustomer(data);
    } catch (error) {
      console.error('Error fetching customer profile:', error);
      setCustomer(null);
    }
  };

  useEffect(() => {
    let refreshInterval: NodeJS.Timeout;

    // Check for existing session immediately
    const checkSession = async () => {
      console.log('Checking existing sessions...');
      
      // First check localStorage for OTP session
      const storedSession = localStorage.getItem('customer_session');
      if (storedSession) {
        try {
          const sessionData = JSON.parse(storedSession);
          console.log('Found session data in localStorage:', sessionData);
          const expiresAt = new Date(sessionData.expires_at);
          
          // Check if session is still valid
          if (expiresAt > new Date()) {
            console.log('Valid OTP session found, setting user and customer');
            setCustomer(sessionData.customer);
            setUser({ email: sessionData.customer.email } as any);
            setLoading(false);
            return;
          } else {
            // Try to refresh expired access token using refresh token
            console.log('OTP session expired, attempting refresh...');
            const refreshResult = await refreshSession();
            if (!refreshResult.error) {
              console.log('Session refreshed successfully');
              return; // refreshSession handles setting state
            } else {
              console.log('Failed to refresh session, removing...');
              localStorage.removeItem('customer_session');
            }
          }
        } catch (error) {
          console.error('Error parsing stored session:', error);
          localStorage.removeItem('customer_session');
        }
      }

      // Fall back to Supabase auth if no valid OTP session
      const { data: { session } } = await supabase.auth.getSession();
      console.log('Checking Supabase session:', session?.user?.email || 'NO EMAIL');
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user?.email) {
        console.log('Found Supabase session, fetching customer...');
        setTimeout(() => {
          fetchCustomer(session.user.email);
        }, 100);
      } else {
        console.log('No sessions found, setting loading to false');
        setLoading(false);
      }
    };

    // Set up auth state listener for Supabase auth (magic links)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Supabase auth state change:', event, session?.user?.email || 'NO EMAIL');
        setSession(session);
        
        if (session?.user?.email) {
          console.log('User authenticated via Supabase, fetching customer...');
          setTimeout(() => {
            fetchCustomer(session.user.email);
          }, 100);
          setUser(session.user);
        } else if (!localStorage.getItem('customer_session')) {
          // Only clear if no OTP session exists
          console.log('No Supabase session and no OTP session, clearing data');
          setUser(null);
          setCustomer(null);
        }
        
        setTimeout(() => {
          setLoading(false);
        }, 200);
      }
    );

    // Auto-refresh OTP sessions every hour
    const setupRefreshInterval = () => {
      refreshInterval = setInterval(() => {
        const storedSession = localStorage.getItem('customer_session');
        if (storedSession) {
          try {
            const sessionData = JSON.parse(storedSession);
            const expiresAt = new Date(sessionData.expires_at);
            
            // Refresh if expires within 5 minutes
            if (expiresAt.getTime() - Date.now() < 5 * 60 * 1000) {
              refreshSession().catch((err) => {
                console.error('Auto-refresh failed:', err);
              });
            }
          } catch (error) {
            console.error('Error checking session for refresh:', error);
          }
        }
      }, 60 * 60 * 1000); // Check every hour
    };

    // Listen for localStorage changes to detect new logins
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'customer_session' && e.newValue) {
        console.log('Detected new session in localStorage via storage event, rechecking...');
        setTimeout(checkSession, 100);
      }
    };

    // Manual session check trigger (for same-window updates)
    const handleManualSessionCheck = () => {
      console.log('Manual session check triggered');
      setTimeout(checkSession, 100);
    };

    // Listen for custom event to trigger session check
    window.addEventListener('portalSessionUpdated', handleManualSessionCheck);

    // Initialize
    checkSession();
    setupRefreshInterval();
    window.addEventListener('storage', handleStorageChange);

    return () => {
      subscription.unsubscribe();
      if (refreshInterval) clearInterval(refreshInterval);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('portalSessionUpdated', handleManualSessionCheck);
    };
  }, []);

  const signInWithMagicLink = async (email: string) => {
    try {
      const redirectUrl = buildAbsoluteUrl('/');
      
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: redirectUrl,
        }
      });

      if (error) {
        toast({
          title: "Sign In Error",
          description: error.message,
          variant: "destructive",
        });
        return { error };
      }

      toast({
        title: "Magic Link Sent",
        description: "Check your email for a secure sign-in link.",
      });

      return { error: null };
    } catch (error: any) {
      toast({
        title: "Sign In Error",
        description: error.message,
        variant: "destructive",
      });
      return { error };
    }
  };

  const signOut = async () => {
    try {
      // Call the logout endpoint to invalidate refresh tokens
      try {
        await supabase.functions.invoke('portal-auth-logout', {
          body: {}
        });
      } catch (logoutError) {
        console.error('Error calling logout endpoint:', logoutError);
        // Continue with logout even if endpoint fails
      }

      // Clear localStorage session
      localStorage.removeItem('customer_session');
      localStorage.removeItem('portal_auth_email');
      
      // Clear React state immediately
      setUser(null);
      setCustomer(null);
      setSession(null);
      
      // Only call Supabase signOut if there's an actual Supabase session
      if (session) {
        const { error } = await supabase.auth.signOut();
        // Only show error if it's not the expected "session not found" error
        if (error && !error.message.toLowerCase().includes('session not found')) {
          toast({
            title: "Sign Out Error",
            description: error.message,
            variant: "destructive",
          });
          return;
        }
      }
      
      // Navigate to portal entry page
      window.location.href = buildPath('/');

    } catch (error: any) {
      // Only show error if it's not the expected "session not found" error
      if (!error.message.toLowerCase().includes('session not found')) {
        toast({
          title: "Sign Out Error",
          description: error.message,
          variant: "destructive",
        });
      } else {
        // Even with session errors, clear state and redirect
        setUser(null);
        setCustomer(null);
        setSession(null);
        window.location.href = buildPath('/');
      }
    }
  };

  const sendLoginCode = async (email: string) => {
    try {
      const { data: response, error } = await supabase.functions.invoke('portal-auth-send-code', {
        body: { email, delivery: 'email' }
      });

      if (error) {
        return { error };
      }

      return { success: true, error: null };
    } catch (error: any) {
      return { error };
    }
  };

  const signInWithCode = async (email: string, code: string) => {
    try {
      console.log('Attempting to sign in with code for email:', email);
      const { data: response, error } = await supabase.functions.invoke('portal-auth-verify-code', {
        body: { email, code }
      });

      if (error) {
        console.error('Code verification error:', error);
        return { error };
      }

      if (response?.success) {
        console.log('Code verification successful, storing session:', response.session);
        
        // Store the session
        localStorage.setItem('customer_session', JSON.stringify(response.session));
        
        // Update state immediately 
        setCustomer(response.session.customer);
        setUser({ email: response.session.customer.email } as any);
        setLoading(false); // Stop loading after successful login
        
        console.log('State updated after successful code verification');
        return { error: null, session: response.session };
      }

      return { error: { message: 'Invalid response' } };
    } catch (error: any) {
      console.error('Code verification exception:', error);
      return { error };
    }
  };

  const refreshSession = async () => {
    try {
      const storedSession = localStorage.getItem('customer_session');
      if (!storedSession) {
        return { error: { message: 'No session to refresh' } };
      }

      const sessionData = JSON.parse(storedSession);
      
      const { data: response, error } = await supabase.functions.invoke('portal-auth-refresh', {
        body: { 
          email: sessionData.customer.email,
          refresh_token: sessionData.refresh_token 
        }
      });

      if (error) {
        localStorage.removeItem('customer_session');
        setUser(null);
        setCustomer(null);
        return { error };
      }

      if (response?.success) {
        // Store the new session
        localStorage.setItem('customer_session', JSON.stringify(response.session));
        
        // Update state
        setCustomer(response.customer);
        setUser({ email: response.customer.email } as any);
        
        return { error: null };
      }

      return { error: { message: 'Failed to refresh session' } };
    } catch (error: any) {
      localStorage.removeItem('customer_session');
      setUser(null);
      setCustomer(null);
      return { error };
    }
  };

  const refetchCustomer = async () => {
    if (user?.email) {
      await fetchCustomer(user.email);
    }
  };

  const value = {
    user,
    session,
    customer,
    loading,
    signInWithMagicLink,
    signInWithCode,
    sendLoginCode,
    refreshSession,
    signOut,
    refetchCustomer,
  };

  return (
    <CustomerAuthContext.Provider value={value}>
      {children}
    </CustomerAuthContext.Provider>
  );
};